import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Modal } from "./Modal";
import { SearchInput } from "./SearchInput";
import { Spinner } from "./Skeleton";
import { mailingListsAPI } from "../../api/endpoints";
import type { MailingList } from "../../types";
import { Users, ChevronRight } from "lucide-react";

// --- Picker for choosing an existing mailing list ---
export function MailingListPickerModal({
  isOpen,
  onClose,
  onPick,
  isSubmitting = false,
}: {
  isOpen: boolean;
  onClose: () => void;
  onPick: (list: MailingList) => void;
  isSubmitting?: boolean;
}) {
  const [search, setSearch] = useState("");

  const PAGE_SIZE = 3000;
  const { data, isLoading } = useQuery({
    queryKey: ["mailing-lists", search],
    queryFn: async ({ signal }) => {
      if (search.trim()) {
        const res = await mailingListsAPI.search(search, signal);
        return res.data;
      }
      const res = await mailingListsAPI.list({ page_size: PAGE_SIZE }, signal);
      return res.data;
    },
    enabled: isOpen,
  });

  const lists = data?.items ?? [];

  return (
    <Modal
      isOpen={isOpen}
      onClose={() => {
        setSearch("");
        onClose();
      }}
      title="Add to Existing List"
      size="md"
    >
      <div className="p-4 space-y-3">
        <SearchInput value={search} onChange={setSearch} placeholder="Search mailing lists..." />
        <div className="max-h-80 overflow-y-auto space-y-1 relative">
          {isSubmitting && (
            <div className="absolute inset-0 z-10 flex items-center justify-center bg-dark-bg/60 rounded-lg">
              <Spinner />
            </div>
          )}
          {isLoading ? (
            <p className="text-sm text-dark-text-secondary text-center py-4">Loading...</p>
          ) : lists.length === 0 ? (
            <p className="text-sm text-dark-text-secondary text-center py-4">
              {search.trim() ? "No matching lists found." : "No mailing lists yet."}
            </p>
          ) : (
            lists.map((list) => (
              <button
                key={list._id}
                disabled={isSubmitting}
                className="w-full text-left px-4 py-3 rounded-lg bg-dark-bg hover:bg-dark-border transition-colors flex items-center justify-between gap-3 disabled:opacity-50"
                onClick={() => onPick(list)}
              >
                <div className="min-w-0">
                  <div className="font-medium text-dark-text-primary text-sm truncate">{list.name}</div>
                  {list.description && (
                    <div className="text-xs text-dark-text-muted truncate">{list.description}</div>
                  )}
                </div>
                <div className="flex items-center gap-2 shrink-0 text-dark-text-muted">
                  <span className="flex items-center gap-1 text-xs">
                    <Users className="w-3.5 h-3.5" />
                    {list.contacts.length}
                  </span>
                  <ChevronRight className="w-4 h-4" />
                </div>
              </button>
            ))
          )}
        </div>
      </div>
    </Modal>
  );
}
